import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
  IconButton,
} from "@mui/material";
import { Air as RepositioningIcon, Close as CloseIcon } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import {
  getSecAssessmentByID,
  updateSecAssessment,
} from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CommonButton from "../../../../../CustomForm/CommonButton";
import CustomSelect from "../../../../../FormFields/CustomSelect";
import { RepositioningOptions } from "./repositioning.config";

const EditRepositioningModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    type: "",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });

  useQuery([selectedID], () => getSecAssessmentByID(selectedID), {
    enabled: !!selectedID,
    onSuccess: (data) => {
      setFormData({
        type: data.type || "",
        notes: data.notes || "",
        date_time: data.date_time || "",
        handover: data.handover || false,
        manager: data.manager || false,
      });
    },
  });

  const { mutate: updateAssessmentMutation } = useMutation(
    (newData) => updateSecAssessment(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Repositioning data updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.repositionings]);
        handleClose();
      },
      onError: (error) => {
        console.error("Error updating Repositioning data", error);
        enqueueSnackbar("Failed to update Repositioning data", {
          variant: "error",
        });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    updateAssessmentMutation(formData);
  };

  const repositioningSelectOptions = RepositioningOptions.map((opt) => ({
    value: opt,
    label: opt,
  }));

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #F2F4F7",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#FAFBFC",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RepositioningIcon
              sx={{ color: theme.palette.primary.main, fontSize: 18 }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: "#1A1A2E" }}
          >
            Edit Repositioning Entry
          </Typography>
          <Chip
            label="Edit"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 500,
              bgcolor: "#DBEAFE",
              color: "#2563EB",
            }}
          />
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "240px" }}>
              <CustomSelect
                value={formData.type || ""}
                options={repositioningSelectOptions}
                label="Repositioning Type *"
                name="type"
                onChange={handleChange}
                searchable={true}
                searchPlaceholder="Search repositioning type..."
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "240px" }}>
              <CustomDateField
                name="date_time"
                label="Date / Time"
                type="datetime-local"
                value={formData.date_time}
                onChange={handleChange}
                required
              />
            </Box>

            <Box sx={{ flex: "1 1 100%" }}>
              <CustomTextArea
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "240px" }}>
              <CustomCheckbox
                label="Share with Manager"
                name="manager"
                checked={formData.manager}
                onClick={handleCheckBox}
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "240px" }}>
              <CustomCheckbox
                label="Handover"
                name="handover"
                checked={formData.handover}
                onClick={handleCheckBox}
              />
            </Box>

            <Box
              sx={{
                flex: "1 1 100%",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 1,
              }}
            >
              <CommonButton type="submit" onSubmit={handleSubmit} />
            </Box>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default EditRepositioningModal;